import { FC, Fragment, useState } from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Button, Box, Typography, CircularProgress, Tooltip, IconButton } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { FileDTO, FileStatus, OrderSettingDTO } from 'TaxAnalyticsHelpers/TaxAnalyticsClient';
import { ApiClient, PrettyBytes } from 'TaxAnalyticsHelpers';
import { DeloitteProgress } from 'TaxAnalyticsUI/Deloitte';
import moment from 'moment-timezone';
import { FabLoader } from '../FabLoader';
import { fileInterruped } from 'TaxAnalyticsHelpers/Upload';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            textTransform: "none",
            textAlign: "start"
        },
        content: {
            flexGrow: 1,
            backgroundColor: theme.palette.background.default
        },
        fullHeight: { height: '100%', display: 'flex' },
        inline: {
            display: 'inline',
        },
        fullList: {
            width: '100%'
        },
        centered: {
            display: 'flex',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center'
        },
        extendedIcon: {
            marginRight: theme.spacing(1),
        },
        fabProgress: {
            color: theme.deloitte.brand,
            position: 'absolute',
            top: 6,
            left: 7,
            zIndex: 1,
        },
        fabProgress2: {
            color: theme.deloitte.brand,
            position: 'absolute',
            top: -9,
            left: -4,
            zIndex: 1,
        }
    })
);

interface IDownloadbleFileButtonProps {
    file: FileDTO;
    setting?: OrderSettingDTO;
}

export const DownloadableFileButton: FC<IDownloadbleFileButtonProps> = (props) => {
    const classes = useStyles();
    const [currentDownload, setCurrentDownload] = useState<number | undefined>();
    const { file, setting } = props;
    const downloadFile = async (file: FileDTO) => {
        var customFileClient = ApiClient.customFileClient((progressEvent: any) => {
            setCurrentDownload(Math.round((progressEvent.loaded / progressEvent.total) * 100));
        });
        var response = await customFileClient.downloadFile(file.id);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.name!);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    const getPrettyDate = (date: Date | undefined): string => {
        {
            if (date === undefined)
                return "";
            var newDate = moment(date);
            return newDate.format("DD/MM/YYYY - HH:mm:ss");
        }
    }

    const displayProgress = () => {
        return file.progress ?? 0 * 100 > 0 ? <DeloitteProgress size={46} value={file.progress ?? 0 * 100} variant={((file.progress ?? 0 * 100 === 100) || fileInterruped(file)) ? 'determinate' : undefined} /> : ''
    }
    return (
        <Box marginBottom={1}>
            <Tooltip title="Télécharger" aria-label="Télécharger" placement="top-start">
                <span>
                    <Button disabled={file.status !== FileStatus.Completed} onClick={async () => await downloadFile(file)} className={classes.root} color="primary">
                        <Grid spacing={2} container alignItems="center">
                            {file.status === FileStatus.Completed &&
                                <Grid>
                                    <Box height="46px" width="46px" justifyContent="center">
                                        <Box className={classes.centered} justifyContent="center">
                                            {!currentDownload &&
                                                <CloudDownloadIcon fontSize="large" />
                                            }
                                            {currentDownload &&
                                                <Fragment>
                                                    <CircularProgress variant="determinate" size={46} className={classes.fabProgress} value={currentDownload} />
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                        component="span"
                                                    >
                                                        {currentDownload}%
                                                    </Typography>
                                                </Fragment>
                                            }
                                        </Box>
                                    </Box>
                                </Grid>
                            }
                            {
                                (file.isUploading || file.status === FileStatus.Paused) &&
                                <Grid>
                                    {displayProgress()}
                                </Grid>
                            }
                            {setting &&
                                <Grid>
                                    <Box height="46px" width="46px" justifyContent="center">
                                        <Box className={classes.centered} justifyContent="center">

                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                component="span"
                                            >
                                                {setting?.setting?.translations![0].name}
                                            </Typography>

                                        </Box>
                                    </Box>
                                </Grid>
                            }
                            <Grid>
                                <Typography
                                    variant="body2"
                                    color="textPrimary"
                                    component="p"
                                >
                                    {file.name}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="textPrimary"
                                    component="span"
                                >
                                    {getPrettyDate(file.creationDate)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="textPrimary"
                                    component="span"
                                    style={{ padding: "0px 8px" }}
                                >
                                    {PrettyBytes(file.size, {})}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Button>
                </span>
            </Tooltip>
        </Box>);
}

export interface IDownloadbleFileVarButtonProps {
    name: string;
    title: string;
    analysisId: string;
    downloadFileName?: string;
    extension: string;
    label?: string;
}

export const DownloadableFileVarButton: FC<IDownloadbleFileVarButtonProps> = (props) => {
    const classes = useStyles();
    const [currentDownload, setCurrentDownload] = useState<number | undefined>();
    const { name, analysisId, downloadFileName, title, extension } = props;
    const downloadFile = async () => {
        var customFileClient = ApiClient.customAnalyticsClient((progressEvent: any) => {
            setCurrentDownload(Math.round((progressEvent.loaded / progressEvent.total) * 100));
        });
        var response = await customFileClient.rawData(downloadFileName ?? name, analysisId, downloadFileName ? extension : null);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${decodeURI(response.fileName ?? "")}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setCurrentDownload(undefined);
    }
    return (
        <Tooltip title={title} aria-label={title} placement="top-start">
            <Grid container alignItems="center" justifyContent="space-between" onClick={async () => await downloadFile()}>
                <Grid>
                    <FabLoader variant="determinate" size={38} value={currentDownload} loading={currentDownload ? true : false} className={classes.fabProgress2}>
                        <IconButton
                            style={{ padding: 0 }}
                            disableRipple
                            aria-label={title}
                            component="span"
                            size="large">
                            <CloudDownloadIcon />
                        </IconButton>
                    </FabLoader>
                </Grid>
                <Grid>
                    {title}
                </Grid>
            </Grid>
        </Tooltip>
    );
}
